import React, { useState } from 'react';
import { ArrowLeftRight, Copy, Trash2 } from 'lucide-react';

const ReverseWordsTool = () => {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [mode, setMode] = useState('words'); // words, sentences, lines
  const [copied, setCopied] = useState(false);

  const reverseText = (text) => {
    if (!text.trim()) {
      setOutput('');
      return;
    }

    let result = '';
    switch (mode) {
      case 'words':
        result = text.split(' ').reverse().join(' ');
        break;
      case 'sentences':
        result = text.split(/(?<=[.!?])\s+/).reverse().join(' ');
        break;
      case 'lines':
        result = text.split('\n').reverse().join('\n');
        break;
      default:
        result = text.split(' ').reverse().join(' ');
    }
    setOutput(result);
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
    reverseText(e.target.value);
  };

  const handleModeChange = (newMode) => {
    setMode(newMode);
    if (input) {
      const text = input;
      let result = '';
      switch (newMode) {
        case 'words':
          result = text.split(' ').reverse().join(' ');
          break;
        case 'sentences':
          result = text.split(/(?<=[.!?])\s+/).reverse().join(' ');
          break;
        case 'lines':
          result = text.split('\n').reverse().join('\n');
          break;
        default:
          result = text.split(' ').reverse().join(' ');
      }
      setOutput(result);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(output);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const clearAll = () => {
    setInput('');
    setOutput('');
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2 mb-4">
        {['words', 'sentences', 'lines'].map((m) => (
          <button
            key={m}
            onClick={() => handleModeChange(m)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              mode === m
                ? 'bg-blue-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            Reverse {m.charAt(0).toUpperCase() + m.slice(1)}
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Input Text
          </label>
          <textarea
            value={input}
            onChange={handleInputChange}
            placeholder="Enter text to reverse words..."
            className="w-full h-64 bg-gray-800 border border-gray-700 rounded-lg p-4 text-white placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Reversed Output
          </label>
          <textarea
            value={output}
            readOnly
            placeholder="Reversed text will appear here..."
            className="w-full h-64 bg-gray-800 border border-gray-700 rounded-lg p-4 text-white placeholder-gray-500"
          />
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={copyToClipboard}
          disabled={!output}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
        >
          <Copy size={16} />
          {copied ? 'Copied!' : 'Copy Result'}
        </button>
        <button
          onClick={clearAll}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        >
          <Trash2 size={16} />
          Clear
        </button>
      </div>

      {input && (
        <div className="p-4 bg-gray-800/50 rounded-lg">
          <h3 className="text-sm font-medium text-gray-300 mb-2">Statistics</h3>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-blue-400">{input.split(' ').filter(w => w).length}</div>
              <div className="text-xs text-gray-500">Words</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-400">{input.split(/[.!?]+/).filter(s => s.trim()).length}</div>
              <div className="text-xs text-gray-500">Sentences</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-purple-400">{input.split('\n').filter(l => l).length}</div>
              <div className="text-xs text-gray-500">Lines</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReverseWordsTool;
